import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type VideoDataSaverContextValue = {
  isVideoDataSaverEnabled: boolean;
  /** @returns neuer Zustand (true = Datensparmodus an) */
  toggleVideoDataSaver: () => boolean;
};

const VideoDataSaverContext = createContext<VideoDataSaverContextValue | null>(
  null
);

export const VIDEO_DATA_SAVER_STORAGE_KEY = "hustle-video-data-saver";

export function VideoDataSaverProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [isVideoDataSaverEnabled, setIsVideoDataSaverEnabled] =
    useState<boolean>(() => {
      try {
        return localStorage.getItem(VIDEO_DATA_SAVER_STORAGE_KEY) === "true";
      } catch {
        return false;
      }
    });
  const enabledRef = useRef(isVideoDataSaverEnabled);

  useEffect(() => {
    enabledRef.current = isVideoDataSaverEnabled;
  }, [isVideoDataSaverEnabled]);

  const toggleVideoDataSaver = useCallback((): boolean => {
    const next = !enabledRef.current;
    enabledRef.current = next;
    try {
      if (next) {
        localStorage.setItem(VIDEO_DATA_SAVER_STORAGE_KEY, "true");
      } else {
        localStorage.removeItem(VIDEO_DATA_SAVER_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
    setIsVideoDataSaverEnabled(next);
    return next;
  }, []);

  return (
    <VideoDataSaverContext.Provider
      value={{ isVideoDataSaverEnabled, toggleVideoDataSaver }}
    >
      {children}
    </VideoDataSaverContext.Provider>
  );
}

export function useVideoDataSaver(): VideoDataSaverContextValue {
  const context = useContext(VideoDataSaverContext);
  if (!context) {
    throw new Error(
      "useVideoDataSaver muss innerhalb von VideoDataSaverProvider verwendet werden"
    );
  }
  return context;
}
